/**
 * React-free helpers behind the ticket's row editors (resource requirements,
 * hazards, link/form attachments).
 *
 * The shapes match what coms stores in the matching JSONB columns and what
 * `actionTicketFields.ts` sanitizes on write, so a row that survives here
 * survives the round trip.
 */

export type ActionResourceRequirement = {
  resourceName: string;
  required: number;
  have: number;
  /** Always derived from required/have, never edited directly. */
  need: number;
  /** Set when the name was picked from the org resource catalog. */
  resourceSetId?: string | null;
  /** ICS 213RR created from this asset row. */
  resourceRequestId?: string | null;
};

export type ActionHazard = {
  hazard: string;
  mitigation: string;
};

export type ActionAttachmentKind = 'link' | 'form' | 'file' | 'folder';

/**
 * A link row is just a URL. A form row points at an exported ICS form file,
 * whose stored `fileUrl` is a storage key rather than something a browser can
 * open, so it is identified by `formId` and carries no URL. A file row points
 * at a file-manager document by `fileId` the same way. A folder row points at a
 * file-manager folder by `folderId`.
 */
export type ActionAttachment = {
  id: string;
  name: string;
  kind: ActionAttachmentKind;
  url?: string;
  formId?: string;
  fileId?: string;
  folderId?: string;
};

export type TaskComment = {
  id: string;
  parent_id: string | null;
  body: string;
  user_guid: string;
  user_name?: string;
  created_at: string;
};

/**
 * One day's progress entry on an HWCG task, with its own comment thread. `date`
 * is a calendar day (`YYYY-MM-DD`), not a timestamp. Optional `status` remains
 * for older stored rows; the UI no longer edits it.
 */
export type DailyProgressReport = {
  id: string;
  date: string;
  summary: string;
  status?: string;
  comments: TaskComment[];
  /** Workspace files/folders attached to this report. */
  attachments: ActionAttachment[];
};

/** HWCG approval stamp; mirrors coms WalaSignature / taskSignatures. */
export type TaskSignature = {
  name: string;
  role: string;
  signedAt: number;
  positionTitle?: string;
  userGuid?: string;
};

/**
 * The ticket fields as they travel on an action, in both directions. Additive
 * and inert while the `ACTION_TICKETS` flag is off, which is what lets an org
 * turn the flag back off without dropping entered data.
 */
export type ActionTicketFieldValues = {
  reporting_location?: string | null;
  requested_arrival_time?: Date | string | null;
  resource_requirements?: ActionResourceRequirement[];
  resource_requirements_na?: boolean;
  hazards?: ActionHazard[];
  attachments?: ActionAttachment[];
  comments?: TaskComment[];
  daily_progress_reports?: DailyProgressReport[];
  signatures?: TaskSignature[];
};

const asRows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (row): row is Record<string, unknown> =>
          !!row && typeof row === 'object'
      )
    : [];

const text = (value: unknown): string =>
  typeof value === 'string' ? value : '';

/** Counts come back off the wire as numbers or numeric strings. */
export const toCount = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
};

/** The shortfall the ticket displays: what is asked for minus what is on hand. */
export const needFor = (required: unknown, have: unknown): number =>
  Math.max(toCount(required) - toCount(have), 0);

export const EMPTY_REQUIREMENT: ActionResourceRequirement = {
  resourceName: '',
  required: 0,
  have: 0,
  need: 0,
};

export const EMPTY_HAZARD: ActionHazard = { hazard: '', mitigation: '' };

export function normalizeRequirements(
  value: unknown
): ActionResourceRequirement[] {
  return asRows(value).map((row) => {
    const resourceSetId = text(row.resourceSetId).trim();
    const resourceRequestId = text(row.resourceRequestId).trim();
    return {
      resourceName: text(row.resourceName),
      required: toCount(row.required),
      have: toCount(row.have),
      need: needFor(row.required, row.have),
      ...(resourceSetId ? { resourceSetId } : {}),
      ...(resourceRequestId ? { resourceRequestId } : {}),
    };
  });
}

export function normalizeHazards(value: unknown): ActionHazard[] {
  return asRows(value).map((row) => ({
    hazard: text(row.hazard),
    mitigation: text(row.mitigation),
  }));
}

export function normalizeAttachments(value: unknown): ActionAttachment[] {
  return asRows(value).flatMap((row) => {
    const url = text(row.url);
    const formId = text(row.formId);
    const fileId = text(row.fileId);
    const folderId = text(row.folderId);
    const kind: ActionAttachmentKind =
      row.kind === 'file'
        ? 'file'
        : row.kind === 'folder'
          ? 'folder'
          : row.kind === 'form'
            ? 'form'
            : 'link';
    if (kind === 'file') {
      if (!fileId) return [];
    } else if (kind === 'folder') {
      if (!folderId) return [];
    } else if (kind === 'form') {
      if (!formId) return [];
    } else if (!url) {
      return [];
    }
    return [
      {
        id: text(row.id) || fileId || folderId || formId || url,
        name: text(row.name) || url,
        kind,
        ...(url ? { url } : {}),
        ...(kind === 'form' ? { formId } : {}),
        ...(kind === 'file' ? { fileId } : {}),
        ...(kind === 'folder' ? { folderId } : {}),
      },
    ];
  });
}

/** Generic immutable row ops, so each section is just render + these three. */
export const addRow = <T>(rows: T[], row: T): T[] => [...rows, row];

export const removeRow = <T>(rows: T[], index: number): T[] =>
  rows.filter((_, i) => i !== index);

export const updateRow = <T>(
  rows: T[],
  index: number,
  patch: Partial<T>
): T[] => rows.map((row, i) => (i === index ? { ...row, ...patch } : row));

/**
 * Requirement edits re-derive `need` on every keystroke rather than storing
 * whatever the user typed, matching the server, which also ignores a submitted
 * `need`.
 */
export function updateRequirement(
  rows: ActionResourceRequirement[],
  index: number,
  patch: Partial<ActionResourceRequirement>
): ActionResourceRequirement[] {
  return rows.map((row, i) => {
    if (i !== index) return row;
    const next = {
      ...row,
      ...patch,
      required: toCount(patch.required ?? row.required),
      have: toCount(patch.have ?? row.have),
    };
    return { ...next, need: needFor(next.required, next.have) };
  });
}

/** Rows the user never filled in are dropped instead of saved as blanks. */
export const requirementsForSubmit = (
  rows: ActionResourceRequirement[]
): ActionResourceRequirement[] =>
  rows.filter(
    (row) => row.resourceName.trim() !== '' || row.required > 0 || row.have > 0
  );

export const hazardsForSubmit = (rows: ActionHazard[]): ActionHazard[] =>
  rows.filter(
    (row) => row.hazard.trim() !== '' || row.mitigation.trim() !== ''
  );

/**
 * `http(s)` only. Anything else — `javascript:`, `data:`, a bare word — is
 * rejected here so the user is told at entry rather than having the row
 * silently dropped by the server.
 */
export function isValidAttachmentUrl(value: string | undefined): boolean {
  const raw = (value ?? '').trim();
  if (!raw) return false;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export const attachmentsForSubmit = (
  rows: ActionAttachment[]
): ActionAttachment[] =>
  rows.filter((row) =>
    row.kind === 'file'
      ? !!row.fileId
      : row.kind === 'folder'
        ? !!row.folderId
        : row.kind === 'form'
          ? !!row.formId
          : isValidAttachmentUrl(row.url)
  );

export function normalizeComments(value: unknown): TaskComment[] {
  return asRows(value).flatMap((row) => {
    const body = text(row.body).trim();
    const user_guid = text(row.user_guid).trim();
    if (!body || !user_guid) return [];
    const parentRaw = text(row.parent_id).trim();
    const createdRaw = text(row.created_at).trim();
    const user_name = text(row.user_name).trim();
    return [
      {
        id: text(row.id).trim() || `${user_guid}-${createdRaw || body}`,
        parent_id: parentRaw || null,
        body,
        user_guid,
        ...(user_name ? { user_name } : {}),
        created_at: createdRaw || new Date().toISOString(),
      },
    ];
  });
}

export const commentsForSubmit = (rows: TaskComment[]): TaskComment[] =>
  rows.filter((row) => !!row.body.trim() && !!row.user_guid.trim());

const DPR_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isCalendarDay = (value: string): boolean => {
  if (!DPR_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
  );
};

/** Today's local calendar day as `YYYY-MM-DD`, the default for a new report. */
export const todayCalendarDay = (now: Date = new Date()): string =>
  [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

/** Daily progress is only writable while the task is being executed. */
export const DAILY_PROGRESS_ACTIVE_STATUS = 'Current/Ongoing';

export const dailyProgressLocked = (
  status: string | null | undefined
): boolean => status !== DAILY_PROGRESS_ACTIVE_STATUS;

/** Newest day first; same-day reports keep their entry order. */
export const sortDailyProgressReports = (
  rows: DailyProgressReport[]
): DailyProgressReport[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) =>
      a.row.date === b.row.date
        ? a.index - b.index
        : a.row.date < b.row.date
          ? 1
          : -1
    )
    .map(({ row }) => row);

/** File/folder rows only on a daily progress report. */
export const dailyProgressAttachmentsForSubmit = (
  rows: ActionAttachment[] | undefined
): ActionAttachment[] =>
  attachmentsForSubmit(rows ?? []).filter(
    (row) => row.kind === 'file' || row.kind === 'folder'
  );

export function normalizeDailyProgressReports(
  value: unknown
): DailyProgressReport[] {
  return asRows(value).flatMap((row) => {
    const date = text(row.date).trim();
    if (!isCalendarDay(date)) return [];
    const summary = text(row.summary).trim();
    const comments = normalizeComments(row.comments);
    const attachments = dailyProgressAttachmentsForSubmit(
      normalizeAttachments(row.attachments)
    );
    if (!summary && comments.length === 0 && attachments.length === 0) {
      return [];
    }
    const status = text(row.status).trim();
    return [
      {
        id: text(row.id).trim() || `dpr-${date}`,
        date,
        summary,
        ...(status ? { status } : {}),
        comments,
        attachments,
      },
    ];
  });
}

/** Rows with no summary, thread, or attachments are dropped instead of saved blank. */
export const dailyProgressReportsForSubmit = (
  rows: DailyProgressReport[]
): DailyProgressReport[] =>
  rows
    .filter((row) => isCalendarDay(row.date))
    .map((row) => {
      const { status: _status, ...rest } = row;
      return {
        ...rest,
        comments: commentsForSubmit(row.comments ?? []),
        attachments: dailyProgressAttachmentsForSubmit(row.attachments),
      };
    })
    .filter(
      (row) =>
        row.summary.trim() !== '' ||
        row.comments.length > 0 ||
        row.attachments.length > 0
    );

/** Total outstanding need across the ticket, for the section header count. */
export const totalNeed = (rows: ActionResourceRequirement[]): number =>
  rows.reduce((sum, row) => sum + needFor(row.required, row.have), 0);
